import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Copy, Download } from "lucide-react";
import { toast } from "sonner";

function AssignDialog({ open, onOpenChange, card, mode, onDone }) {
  const [businesses, setBusinesses] = useState([]);
  const [mode2, setMode2] = useState("existing"); // existing | new
  const [businessId, setBusinessId] = useState("");
  const [nb, setNb] = useState({ business_name: "", owner_name: "", phone: "", email: "" });
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/businesses").then((r) => setBusinesses(r.data));
      setLocation(card.location || "");
      setUrl(mode === "reassign" ? "" : card.google_review_url || "");
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    const payload = { location, google_review_url: url };
    if (mode2 === "existing") payload.business_id = businessId;
    else Object.assign(payload, nb);
    try {
      await api.post(`/cards/${card.card_code}/${mode}`, payload);
      toast.success(mode === "assign" ? "Card assigned & activated" : "Card reassigned");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "assign" ? "Assign Card" : "Reassign Card"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button variant={mode2 === "existing" ? "default" : "outline"} size="sm" onClick={() => setMode2("existing")} data-testid="assign-existing-tab">
              Existing Business
            </Button>
            <Button variant={mode2 === "new" ? "default" : "outline"} size="sm" onClick={() => setMode2("new")} data-testid="assign-new-tab">
              New Business
            </Button>
          </div>

          {mode2 === "existing" ? (
            <div className="space-y-1.5">
              <Label>Business</Label>
              <Select value={businessId} onValueChange={setBusinessId}>
                <SelectTrigger data-testid="assign-business-select"><SelectValue placeholder="Select business" /></SelectTrigger>
                <SelectContent>
                  {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {[["business_name", "Business Name *"], ["owner_name", "Owner *"], ["phone", "Phone *"], ["email", "Email"]].map(([k, l]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{l}</Label>
                  <Input value={nb[k]} onChange={(e) => setNb({ ...nb, [k]: e.target.value })} data-testid={`assign-nb-${k}`} />
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Location (e.g. Kasir, Front Desk, Table 01)</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="assign-location-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Google Review URL (https)</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://g.page/r/..." data-testid="assign-url-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="assign-submit-button">
            {saving ? "Saving..." : mode === "assign" ? "Assign & Activate" : "Reassign & Activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CardDetail() {
  const { cardCode } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("assign");
  const [editUrl, setEditUrl] = useState("");

  const load = useCallback(() => {
    api.get(`/cards/${cardCode}`)
      .then((r) => { setCard(r.data); setEditUrl(r.data.google_review_url || ""); })
      .catch(() => toast.error("Failed to load card"));
  }, [cardCode]);
  useEffect(() => { load(); }, [cardCode]);

  const action = async (name) => {
    try {
      await api.post(`/cards/${cardCode}/${name}`);
      toast.success(`Card ${name}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveUrl = async () => {
    try {
      await api.patch(`/cards/${cardCode}`, { google_review_url: editUrl });
      toast.success("Review URL updated");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleNfc = async () => {
    await api.patch(`/cards/${cardCode}`, { nfc_programmed: !card.nfc_programmed });
    load();
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Copied"); }
      catch (_) { toast.error("Copy failed — select and copy manually"); }
      document.body.removeChild(ta);
    }
  };

  if (!card) return <div className="p-8">Loading...</div>;

  const rows = [
    ["Card ID", <span className="font-mono">{card.card_code}</span>],
    ["Status", <StatusBadge status={card.status} />],
    ["Business", card.business?.business_name || "—"],
    ["Owner", card.business?.owner_name || "—"],
    ["Phone", card.business?.phone || "—"],
    ["Location", card.location || "—"],
    ["Created", new Date(card.created_at).toLocaleString()],
    ["Activated", card.activated_at ? new Date(card.activated_at).toLocaleString() : "—"],
  ];

  return (
    <div data-testid="card-detail-page">
      <button className="flex items-center gap-1 text-sm text-gray-500 mb-4" onClick={() => navigate("/cards")} data-testid="back-button">
        <ArrowLeft size={16} /> Back to Cards
      </button>
      <h1 className="text-2xl font-bold mb-6 font-mono">{card.card_code}</h1>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Card Details</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <tbody>
                  {rows.map(([k, v], i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 text-gray-500 w-40">{k}</td>
                      <td className="py-2">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Google Review URL</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {card.business_id ? (
                <>
                  <div className="flex gap-2">
                    <Input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} data-testid="edit-review-url-input" />
                    <Button onClick={saveUrl} data-testid="save-review-url-button">Save</Button>
                  </div>
                  <p className="text-xs text-gray-400">Changing this does NOT change the card's QR/URL.</p>
                </>
              ) : (
                <p className="text-sm text-gray-400">Assign the card to a business to set a review URL.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Lifecycle Actions</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {(card.status === "UNASSIGNED" || card.status === "RESERVED") && card.status !== "RETIRED" && !card.business_id && (
                <Button onClick={() => { setAssignMode("assign"); setAssignOpen(true); }} data-testid="assign-card-button">Assign Card</Button>
              )}
              {card.business_id && card.status !== "RETIRED" && (
                <Button onClick={() => { setAssignMode("reassign"); setAssignOpen(true); }} data-testid="reassign-card-button">Reassign</Button>
              )}
              {card.status === "ACTIVE" && (
                <Button variant="outline" onClick={() => action("suspend")} data-testid="suspend-card-button">Suspend</Button>
              )}
              {(card.status === "SUSPENDED" || card.status === "RESERVED") && card.business_id && (
                <Button variant="outline" onClick={() => action("activate")} data-testid="activate-card-button">Activate</Button>
              )}
              {card.status === "UNASSIGNED" && (
                <Button variant="outline" onClick={() => action("reserve")} data-testid="reserve-card-button">Reserve</Button>
              )}
              {card.business_id && card.status !== "RETIRED" && (
                <Button variant="outline" onClick={() => action("unassign")} data-testid="unassign-card-button">Unassign</Button>
              )}
              {card.status !== "RETIRED" && (
                <Button variant="destructive" onClick={() => action("retire")} data-testid="retire-card-button">Retire</Button>
              )}
            </CardContent>
          </Card>

          {card.history?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Assignment History</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 border-b">
                    <th className="py-2">Business</th><th>Assigned</th><th>Unassigned</th><th>By</th>
                  </tr></thead>
                  <tbody>
                    {card.history.map((h) => (
                      <tr key={h.id} className="border-b last:border-0" data-testid={`history-row-${h.id}`}>
                        <td className="py-2">{h.business?.business_name || "—"}</td>
                        <td>{new Date(h.assigned_at).toLocaleDateString()}</td>
                        <td>{h.unassigned_at ? new Date(h.unassigned_at).toLocaleDateString() : "current"}</td>
                        <td className="text-gray-500">{h.assigned_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">QR Code</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="border rounded-lg p-3 bg-white flex justify-center">
                <img src={`${API}/cards/${card.card_code}/qr?format=png`} alt="QR" className="w-40 h-40" data-testid="qr-preview" />
              </div>
              <div className="flex gap-2">
                <a href={`${API}/cards/${card.card_code}/qr/download?format=png`} className="flex-1">
                  <Button variant="outline" className="w-full" data-testid="download-png-button"><Download size={16} className="mr-1" /> PNG</Button>
                </a>
                <a href={`${API}/cards/${card.card_code}/qr/download?format=svg`} className="flex-1">
                  <Button variant="outline" className="w-full" data-testid="download-svg-button"><Download size={16} className="mr-1" /> SVG</Button>
                </a>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Permanent Public URL</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="text-xs font-mono break-all bg-gray-50 p-2 rounded border" data-testid="public-url-text">
                {card.public_url}
              </div>
              <Button variant="outline" className="w-full" onClick={() => copy(card.public_url)} data-testid="copy-url-button">
                <Copy size={16} className="mr-1" /> Copy URL (QR + NFC)
              </Button>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!card.nfc_programmed} onChange={toggleNfc} data-testid="nfc-programmed-checkbox" />
                NFC programmed
              </label>
            </CardContent>
          </Card>
        </div>
      </div>

      <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} card={card} mode={assignMode} onDone={load} />
    </div>
  );
}
